"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

import VoiceQueryInput from "./VoiceQueryInput";

export interface InstantQuestionConsultationWidgetProps {
  title?: string;
  subtitle?: string;
  defaultCategory?: "career" | "marriage" | "dasha" | "remedy";
  className?: string;
}

const SUGGESTED_QUESTIONS: Array<{ text: string; category: string }> = [
  { text: "When will my career transition and financial growth accelerate?", category: "career" },
  {
    text: "What planetary period governs my marriage and relationship timing?",
    category: "marriage",
  },
  {
    text: "Which specific Upay is prescribed for my current Mahadasha / Antardasha?",
    category: "dasha",
  },
  { text: "What astrological factors are causing delays in my undertakings?", category: "remedy" },
];

/**
 * Direct Conversational Entrypoint for AI Astrology queries.
 *
 * Converts search intent from "free astrology chat" and "ai astrologer"
 * directly into chart-grounded consultations.
 */
export default function InstantQuestionConsultationWidget({
  title = "Ask AI Jyotishi a Question",
  subtitle = "Receive an immediate Vedic astrological answer analyzed against your planetary positions and current Dasha.",
  className = "",
}: InstantQuestionConsultationWidgetProps) {
  const [selectedQuestion, setSelectedQuestion] = useState(SUGGESTED_QUESTIONS[0].text);
  const [customQuestion, setCustomQuestion] = useState("");

  const activeQuestion = customQuestion.trim() || selectedQuestion;

  // `/chat/` has no index route — only `/chat/[readingId]` — so the previous
  // `/chat/?q=` target was a hard 404 and no surface ever read the `q` param:
  // the seeker's typed question was collected and discarded. The consultation
  // entrypoint is owned by the SEO intent registry, which resolves the
  // locale-correct customer surface for wherever this widget is mounted.
  const sourcePath = usePathname() || "/free-kundali";
  const config = getSEOIntentConfigForSource(sourcePath);
  const chatHref = buildSEOAgentHref(config, sourcePath);

  // The chat auto-sends seo_entry_prompt as its first message, so writing the
  // typed question here is what makes the next screen open on the question the
  // seeker actually asked instead of a generic greeting.
  const handleConsult = () => {
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", sourcePath);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", activeQuestion);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage unavailable — the link still opens the chat, just without auto-send.
    }
  };

  return (
    <section
      aria-labelledby="instant-question-heading"
      className={`my-8 rounded-2xl border border-border-default bg-surface-raised p-6 shadow-xs ${className}`}
    >
      <div className="mb-4">
        <h3
          id="instant-question-heading"
          className="text-title font-serif font-bold text-text-primary"
        >
          {title}
        </h3>
        {subtitle && <p className="mt-1 text-body-small text-text-secondary">{subtitle}</p>}
      </div>

      <div className="space-y-3">
        <label className="text-caption font-semibold uppercase tracking-wider text-text-primary block">
          Choose a common Jyotish question or type your own:
        </label>

        <div className="flex flex-wrap gap-2">
          {SUGGESTED_QUESTIONS.map((q) => {
            const isSelected = selectedQuestion === q.text && !customQuestion;
            return (
              <button
                key={q.text}
                type="button"
                onClick={() => {
                  setSelectedQuestion(q.text);
                  setCustomQuestion("");
                }}
                className={`rounded-xl border px-3 py-2 text-caption text-left transition-all ${
                  isSelected
                    ? "border-intent-brand bg-intent-brand/10 font-bold text-intent-brand"
                    : "border-border-default bg-surface-warm text-text-secondary hover:border-border-interactive hover:text-text-primary"
                }`}
              >
                {q.text}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <input
            type="text"
            value={customQuestion}
            onChange={(e) => setCustomQuestion(e.target.value)}
            placeholder="Or type your specific question here (e.g. When will I get a promotion?)..."
            className="flex-1 rounded-xl border border-border-default bg-surface-warm px-4 py-3 text-body-small text-text-primary placeholder:text-text-muted focus:border-intent-brand focus:outline-hidden"
          />
          <VoiceQueryInput
            onTranscript={(text) => setCustomQuestion(text)}
            className="shrink-0 justify-center"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border-default pt-4">
          <span className="text-caption text-text-muted">
            Grounded in Sage Parashara principles • 100% Private
          </span>
          <Link
            href={chatHref}
            onClick={handleConsult}
            className="inline-flex items-center justify-center rounded-xl bg-intent-brand px-5 py-2.5 text-body-small font-bold text-text-on-brand shadow-xs transition-opacity hover:opacity-90"
          >
            Ask with Chart Context →
          </Link>
        </div>
      </div>
    </section>
  );
}
